import type { JobDetailsData, RepairKitsSection } from "@stryker/core/types/compositeSections";
import type { ExpandStatus } from "@stryker/core/types/expandable";
import type { ExpandableCards } from "@stryker/core/types/jobs";
import { useEffect, useState } from "react";
import { isEmpty } from "remeda";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Typography } from "@/components/ui/typography";
import { useConfigOptions } from "@/hooks/useConfigOptions";
import ExpandableSection from "../../../../components/job/ExpandableSection";

const cardName = "repairKits";

type RepairKitsProps = {
  savedRepairKits: RepairKitsSection;
  toggleExpandableSection: (section: ExpandableCards) => void;
  setRepairKits: (payload: { data: { repair_length_repair_kit_sizes: Array<number> }; status: ExpandStatus }) => void;
  jobDetailsData: JobDetailsData;
};

export function RepairKits({
  toggleExpandableSection,
  savedRepairKits,
  setRepairKits,
  jobDetailsData,
}: RepairKitsProps) {
  const { getKitOptions } = useConfigOptions();
  const kitsOptions = getKitOptions(jobDetailsData.repair_system);

  // Initialize state from saved data, updating when savedRepairKits changes
  const [repair_length_repair_kit_sizes, setRepair_length_repair_kit_sizes] = useState<Array<number>>([]);

  // Sync state with saved data and clear when repair system changes
  useEffect(() => {
    if (savedRepairKits.isDataFilled && savedRepairKits.data.repair_length_repair_kit_sizes.length > 0) {
      setRepair_length_repair_kit_sizes(savedRepairKits.data.repair_length_repair_kit_sizes);
      return;
    }
    setRepair_length_repair_kit_sizes([]);
  }, [jobDetailsData.repair_system, savedRepairKits.isDataFilled, savedRepairKits.data.repair_length_repair_kit_sizes]);

  function handleSave() {
    if (isEmpty(kitsOptions) || repair_length_repair_kit_sizes.length > 0) {
      setRepairKits({
        data: { repair_length_repair_kit_sizes },
        status: "done",
      });
    }
  }

  const isSaveDisabled = !isEmpty(kitsOptions) && !repair_length_repair_kit_sizes.length;

  return (
    <ExpandableSection
      status={savedRepairKits.status}
      setIsExpanded={() => toggleExpandableSection(cardName)}
      title="Kits"
    >
      <div className="flex flex-col gap-4">
        <Typography variant="text-small" className="mb-4 text-gray-600">
          Check all or ones you would like to add if any
        </Typography>
        <Typography variant="text-small" weight="semibold" className="mb-4 text-primary-500">
          At least one kit is required
        </Typography>
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {kitsOptions.map((kit) => {
            function handleCheckboxChange(value: boolean) {
              setRepair_length_repair_kit_sizes((prevState) =>
                value ? [...prevState, kit.value] : prevState.filter((v) => v !== kit.value)
              );
            }

            return (
              <Checkbox
                key={kit.value}
                checked={repair_length_repair_kit_sizes.includes(kit.value)}
                onChange={handleCheckboxChange}
                label={kit.title}
              />
            );
          })}
        </div>
        <Button className="ml-auto self-end" onClick={handleSave} disabled={isSaveDisabled}>
          Save
        </Button>
      </div>
    </ExpandableSection>
  );
}
